import React from 'react'
import { useIntl } from 'react-intl'
import { useParams, useLocation } from 'react-router-dom'
import styled from '@emotion/styled'
import { Chip, Divider, LinearProgress } from '@mui/material'
import { Box } from '@mui/system'
import { H3 } from '../../../layouts/TextStyles'
import CircularProgressWithLabel from '../../../components/muiComponents/circularProgressWithLabel'
import { testApiSlice } from '../../../features/section/testSlice'
import ResultQuestion from './components/ResultQuestion'
import ResultNavigation from './components/ResultNavigation'
import Breadcrumb from '../../../components/Breadcrumb'
import { capitalize } from '../../../utils/functions'

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'start',
        mt: 4,
    },
    main: {
        width: '100%',
        maxWidth: '900px',
        pb: 10,
        mr: 2,
        maxHeight: '65vh',
        overflow: 'auto',
    },
    aside: {
        height: '475px',
        width: '100%',
        maxWidth: '270px',
        position: 'sticky',
        marginTop: '80px',
        top: '10px',
    },
    incorrect: {
        display: 'flex',
        fontWeight: 600,
        fontSize: 22,
        color: '#EA4F74',
    },
    correct: {
        display: 'flex',
        fontWeight: 600,
        fontSize: 22,
        color: '#00B998',
        mb: 1,
    },
    resultChartBox: {
        display: 'flex',
        alignItems: 'center',
        mb: 6,
    },
    chipIncorrect: {
        fontWeight: 600,
        fontSize: 18,
        color: '#EA4F74',
        width: '60px',
        background: '#FEF5F5',
        border: '1px solid #EA4F74',
    },
    chipCorrect: {
        fontWeight: 600,
        fontSize: 18,
        color: '#00B998',
        width: '60px',
        background: '#E6FCF4',
        border: '1px solid #00B998',
    },
    subtitle: {
        fontWeight: 700,
        fontSize: '20px',
        lineHeight: '25px',
        color: '#627480',
        mb: 2,
    },
}

const ExamResultPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const courseTitle = state?.course
        ? state?.course?.[`title${capitalize(locale)}`]
        : ''
    const subjectTitle = state?.subject
        ? state?.subject?.[`title${capitalize(locale)}`]
        : ''
    const { topicTestId } = useParams()
    const { data: result = null, isFetching } =
        testApiSlice.useGetStudentTestResultQuery(+topicTestId)

    const resultPercentage = () => {
        const { countCorrect, countWrong } = result
        const totalQuestions = countCorrect + countWrong

        return Math.round((countCorrect * 100) / totalQuestions)
    }

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'myCourses' }),
                    courseTitle,
                    subjectTitle,
                    formatMessage({ id: 'workOnMistake' }),
                ]}
            />
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'exam_results' })}</H3>
                    </Title>
                </Header>

                {isFetching ? (
                    <LinearProgress />
                ) : result ? (
                    <>
                        <Box sx={styles.subtitle}>{result.topicTestTitle}</Box>

                        <Box sx={styles.resultChartBox}>
                            <Box sx={{ mr: 4 }}>
                                <CircularProgressWithLabel
                                    variant="determinate"
                                    value={resultPercentage()}
                                    color="secondary"
                                />
                            </Box>

                            <Box>
                                <Box sx={styles.correct}>
                                    <Box sx={{ width: '170px' }}>
                                        {formatMessage({ id: 'correct' })}:
                                    </Box>

                                    <Chip
                                        label={result.countCorrect}
                                        sx={styles.chipCorrect}
                                    />
                                </Box>

                                <Box sx={styles.incorrect}>
                                    <Box sx={{ width: '170px' }}>
                                        {formatMessage({ id: 'wrong' })}:
                                    </Box>

                                    <Chip
                                        label={result.countWrong}
                                        sx={styles.chipIncorrect}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={styles.subtitle}>
                            {formatMessage({ id: 'yourAnswers' })}
                        </Box>

                        <Divider />

                        <Box sx={styles.container}>
                            <Box sx={styles.main}>
                                {result.questions.map((question, index) => (
                                    <ResultQuestion
                                        key={question.id}
                                        index={index}
                                        question={question}
                                    />
                                ))}
                            </Box>

                            <ResultNavigation questions={result.questions} />
                        </Box>
                    </>
                ) : (
                    formatMessage({ id: 'noData' })
                )}
            </Content>
        </Wrapper>
    )
}

export default ExamResultPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px 40px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
